import {match, Pattern} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {CustomerResponseBodyV2, PersonResponseBodyV2} from '@dms/api/customer';
import {getCustomerEmail, getCustomerName, getCustomerPhoneNumber} from '@dms/shared';

import type {CustomerInsightsIdentity} from '../types/CustomerInsightsViewModel';

const getCustomerContact = (customer: CustomerResponseBodyV2): PersonResponseBodyV2 | null =>
  match(customer.discriminator)
    .with('NATURAL_PERSON', () => customer.foundingPerson)
    .with('BUSINESS', () => customer.contacts[0] ?? null)
    .exhaustive();

const mapCustomerPhone = (customer: CustomerResponseBodyV2) => {
  const contact = getCustomerContact(customer);
  const phoneNumber = contact?.phoneNumbers[0];

  return match(phoneNumber)
    .when(
      (value) => isNotNilOrEmpty(value?.prefix) && isNotNilOrEmpty(value?.number),
      () => getCustomerPhoneNumber(customer) ?? undefined
    )
    .otherwise(() => undefined);
};

const mapCustomerEmail = (customer: CustomerResponseBodyV2) => {
  const email = getCustomerContact(customer)?.emails[0]?.email;

  return match(email)
    .when(isNotNilOrEmpty, () => getCustomerEmail(customer) ?? undefined)
    .otherwise(() => undefined);
};

export function mapCustomerIdentity(customer: CustomerResponseBodyV2): CustomerInsightsIdentity {
  const name = getCustomerName(customer);

  return {
    id: customer.id,
    publicId: customer.publicId ?? undefined,
    name: match(name)
      .with(Pattern.string, (value) => value)
      .otherwise(() => customer.id),
    phone: mapCustomerPhone(customer),
    email: mapCustomerEmail(customer),
  };
}
